
import { Vue, Options } from "vue-class-component";
import CreateCargoNonRtcLayout from "./create-cargo-non-rtc-layout.vue";
import ScanTableSection from "../components/scan-table-section.vue";
import SectionSelectDestCargoType from "../components/section-select-dest-cargo-type.vue";
import CargoDisputeForm from "../components/cargo-dispute-form/index.vue";
import { CargoV2Controller } from "@/app/ui/controllers/CargoV2Controller";
import CargoV2ManualInput from "../components/manual-input/cargo-v2-manual-input.vue";
import {
  CalculateVolumeApiRequestV2,
  CalculateVolumeRequestV2,
  CreateCargoV3ApiRequest
} from "@/data/payload/api/CargoV2ApiRequest";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { Snackbar } from "@/domain/entities/MainApp";
import {
  CalculateVolume,
  CreateCargoV3,
  FormCargoV2
} from "@/domain/entities/CargoV2";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import { CheckEligible, useCargoV2 } from "../composables";
import { isPlaneNogValid } from "../utils";
import { cloneDeep } from "lodash";

const { onCheckEligible } = useCargoV2();

@Options({
  components: {
    CreateCargoNonRtcLayout,
    ScanTableSection,
    SectionSelectDestCargoType,
    CargoDisputeForm,
    CargoV2ManualInput
  },
  beforeRouteLeave(to, _from, next) {
    if (to.name !== "cargo-v2-create-manual-search-flight") {
      CargoV2Controller.setForm(new FormCargoV2());
      CargoV2Controller.setCalculateVolumeData(new CalculateVolume());
      CargoV2Controller.setUploadProofPhoto(false);
    }
    next();
  },
  watch: {
    isUploadProofPhoto: {
      handler(bool: boolean) {
        if (!bool) CargoV2Controller.setForm({ photoProof: [] });
      }
    }
  }
})
class CargoV2CreateManual extends Vue {
  get form() {
    return CargoV2Controller.form;
  }

  get isValidSearchFlight() {
    const { cargoType, cargoNo } = this.form;

    return [cargoType.value === "plane", !cargoNo, this.isValidForm].every(
      Boolean
    );
  }

  get isValidBooking() {
    const { isValidAwbManual, cargoNo, cargoType } = this.form;

    const isShipValid = cargoType.value !== "ship" || cargoNo;
    const isPlaneValid = cargoType.value !== "plane" || isValidAwbManual;

    return [isShipValid, isPlaneValid, this.isValidForm].every(Boolean);
  }
  get isValidForm() {
    const {
      destinationCityCode,
      cargoType,
      isValidVehicleNo,
      cargoCommodity,
      productCargo,
      bagOrStt,
      cargoActualPiece,
      actualTotalCargoGrossweigth,
      actualTotalCargoVolumeweight,
      actualDimension: {
        cargoActualHeightDimension,
        cargoActualLengthDimension,
        cargoActualWidthDimension
      },
      photoProof
    } = this.form;

    const {
      isGrossWeightInputError,
      isVolumeWeightError,
      isPiecesInputError,
      isUploadProofPhoto,
      isExceedMaxDisputeWeight
    } = CargoV2Controller;

    const isValidVehicle = isValidVehicleNo || cargoType.value === "plane";

    const isValidDispute = [
      cargoActualPiece,
      !isGrossWeightInputError,
      !isVolumeWeightError,
      !isPiecesInputError,
      actualTotalCargoGrossweigth,
      actualTotalCargoVolumeweight,
      cargoActualHeightDimension,
      cargoActualLengthDimension,
      cargoActualWidthDimension,
      photoProof.filter(Boolean).length || !isUploadProofPhoto,
      !isExceedMaxDisputeWeight
    ].every(Boolean);

    const isNogValid = isPlaneNogValid(cargoType.value, this.form.nog);

    return [
      destinationCityCode.code,
      cargoType.value,
      isValidVehicle,
      cargoCommodity.code,
      productCargo,
      isValidDispute,
      bagOrStt.length,
      isNogValid
    ].every(Boolean);
  }

  get isUploadProofPhoto() {
    return CargoV2Controller.isUploadProofPhoto;
  }

  onProcessSearchFlight() {
    onCheckEligible(new CheckEligible({ success: this.onSearchFlight }));
  }

  onSearchFlight() {
    this.$router.push("/cargo-v2/create/manual/search-flight");
  }

  async onCalculateVolume() {
    return await CargoV2Controller.calculateVolume(
      new CalculateVolumeApiRequestV2({
        calculateType: CargoV2Controller.calculateType,
        calculateRequest: new CalculateVolumeRequestV2({
          length: 90,
          width: 60
        })
      })
    );
  }

  onProcessBooking() {
    onCheckEligible(new CheckEligible({ success: this.onBooking }));
  }

  keySuccess = 0;
  async onBooking() {
    try {
      window.requestAnimationFrame(() => {
        MainAppController.showLoading();
      });
      MainAppController.closeMessageModal();
      MainAppController.closeErrorMessage();
      await this.onCalculateVolume();

      if (this.form.cargoType.value !== "plane") {
        CargoV2Controller.setDefaultDepartureArrivalNonPlane();
      }

      const res = await CargoV2Controller.createBooking(
        new CreateCargoV3ApiRequest({
          ...this.form,
          totalStt: CargoV2Controller.totalSttDataTable,
          totalPiece: this.form.totalAccumulationActualPieceDataTable,
          isUseNewCalculation: true,
          calculateType: CargoV2Controller.calculateType
        })
      );

      this.onSuccessCreate(res);

      this.keySuccess++;
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          AccountController.accountData.accountIsForeign
            ? this.$t("cargoV2.create.error.failedToCargoBooking")
            : this.$t("cargoV2.create.error.creationFailed"),

          this.onBooking
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  onSuccessCreate(resp: CreateCargoV3) {
    const { destinationCityCode } = cloneDeep(this.form);
    MainAppController.setSnackbarData(
      new Snackbar({
        type: "success",
        message: this.$t("cargoV2.create.snackbar.booked", {
          cargoNo: resp.cargoNo,
          destination: destinationCityCode.code
        })
      })
    );
    MainAppController.setAutoClose(true);
    MainAppController.setSnackbar(true);

    CargoV2Controller.setForm(new FormCargoV2());
    CargoV2Controller.setCalculateVolumeData(new CalculateVolume());
    CargoV2Controller.setUploadProofPhoto(false);
  }
}
export default CargoV2CreateManual;
